import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Tooltip,
  CircularProgress,
  Alert,
  TablePagination,
  Stack,
  useTheme,
  Button,
  Snackbar,
  Chip,
  Checkbox,
  ListItemText,
  OutlinedInput
} from '@mui/material';
import {
  Event,
  Refresh,
  Search,
  Visibility,
  CalendarMonth,
  Person,
  Assignment,
  Block,
  FilterList,
  CheckCircle,
  Warning,
  Add,
  Close
} from '@mui/icons-material';
import { useAuthToken } from '../TokenContext';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import axios from 'axios';

const MeetingSummariesTableTalent = ({collapsed}) => {
  const intl = useIntl();
  const theme = useTheme();
  const [meetings, setMeetings] = useState([]);
  const [filteredMeetings, setFilteredMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [timeRange, setTimeRange] = useState('all');
  const [selectedCandidate, setSelectedCandidate] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [contentFilter, setContentFilter] = useState('all');
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const token = useAuthToken();
  
  // New states for add meeting dialog
  const [addMeetingDialog, setAddMeetingDialog] = useState(false);
  const [postingMeeting, setPostingMeeting] = useState(false);
  const [postSuccess, setPostSuccess] = useState(false);
  const [pods, setPods] = useState([]);
  const [loadingPods, setLoadingPods] = useState(false);
  const [podEntries, setPodEntries] = useState({});
  const [podSearchQuery, setPodSearchQuery] = useState('');

  // Ensure pods is always an array
  const safePods = Array.isArray(pods) ? pods : [];

  // Filter pods based on search query
  const filteredPods = safePods.filter(pod => {
    if (!podSearchQuery.trim()) return true;
    
    const searchLower = podSearchQuery.toLowerCase();
    return (
      pod.pod_name?.toLowerCase().includes(searchLower) ||
      pod.pod_id?.toString().includes(searchLower) ||
      pod.art_name?.toLowerCase().includes(searchLower)
    );
  });

  const { user } = useSelector(state => state.auth);

  // Fetch pods from API
  const fetchPods = async () => {
    try {
      setLoadingPods(true);
      console.log('Making API call to fetch pods...');
      const requestData = { c_id: user.data[0].c_id };
      console.log('Request data:', requestData);
      console.log('Headers:', { Authorization: token });

      const response = await axios.post(
        process.env.REACT_APP_TALENT_GETPODSFORMETRICSTALENT,
        requestData,
        { headers: { Authorization: token } }
      );

      const data = response.data;
      console.log('Pods API Response:', data);
      console.log('Response data type:', typeof data);
      console.log('Response data body:', data?.body);
      console.log('Body type:', typeof data?.body);
      console.log('Is body array:', Array.isArray(data?.body));
      
      // Handle the response structure: { statusCode: 200, body: [...] }
      let podsData = [];
      if (data && data.body && Array.isArray(data.body)) {
        podsData = data.body;
      } else if (Array.isArray(data)) {
        // Fallback if response is directly an array
        podsData = data;
      } else {
        console.warn('Unexpected pods API response structure:', data);
        podsData = [];
      }
      
      console.log('Final pods data:', podsData);
      console.log('Setting pods to:', podsData);
      console.log('Is podsData array?', Array.isArray(podsData));
      setPods(Array.isArray(podsData) ? podsData : []);
      
      // Initialize pod entries when pods are loaded
      if (Array.isArray(podsData) && podsData.length > 0) {
        const initialPodEntries = {};
        podsData.forEach(pod => {
          initialPodEntries[pod.pod_id] = {
            checked: false,
            url: ''
          };
        });
        setPodEntries(initialPodEntries);
      }
    } catch (err) {
      console.error('Failed to fetch pods:', err);
      setError('Failed to fetch pods: ' + err.message);
      setPods([]); // Ensure pods is always an array even on error
    } finally {
      setLoadingPods(false);
    }
  };

  // Open add meeting dialog and fetch pods
  const openAddMeetingDialog = () => {
    setAddMeetingDialog(true);
    fetchPods();
  };

  // Close dialog and reset form
  const closeAddMeetingDialog = () => {
    setAddMeetingDialog(false);
    setPodSearchQuery('');
    // Reset all pod entries
    const resetPodEntries = {};
    safePods.forEach(pod => {
      resetPodEntries[pod.pod_id] = {
        checked: false,
        url: ''
      };
    });
    setPodEntries(resetPodEntries);
  };

  // Update pod entry (checkbox or URL)
  const updatePodEntry = (podId, field, value) => {
    setPodEntries(prev => ({
      ...prev,
      [podId]: {
        ...prev[podId],
        [field]: value
      }
    }));
  };

  // Validate pod entries - at least one pod checked with URL
  const validatePodEntries = () => {
    return Object.values(podEntries).some(entry => 
      entry.checked && entry.url.trim() !== ''
    );
  };

  // Get checked pods with their URLs
  const getCheckedPodsData = () => {
    const checkedPods = [];
    Object.entries(podEntries).forEach(([podId, entry]) => {
      if (entry.checked && entry.url.trim()) {
        checkedPods.push({
          podId: parseInt(podId),
          url: entry.url.trim()
        });
      }
    });
    return checkedPods;
  };

  const postMeetingUrl = async () => {
    if (!validatePodEntries()) return;
    
    try {
      setPostingMeeting(true);
      setPostSuccess(false);
      setError(null);
      
      // Get checked pods data
      const checkedPodsData = getCheckedPodsData();
      
      // Build payload arrays - maintain order correlation
      const pod_ids = checkedPodsData.map(item => item.podId);
      const meeting_urls = checkedPodsData.map(item => item.url);
      
      const payload = {
        pod_ids,
        meeting_urls
      };
      
      console.log('Posting meetings with payload:', payload);
      console.log('Checked pods data:', checkedPodsData);
      
      const response = await fetch('https://7549jb05eg.execute-api.us-east-1.amazonaws.com/prod/recallai', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(`Failed to post meeting URLs: ${response.status}`);
      }

      const totalPods = pod_ids.length;
      const uniqueUrls = [...new Set(meeting_urls)].length;
      
      setPostSuccess({
        message: `Successfully added ${uniqueUrls} unique URL(s) to ${totalPods} pod(s)!`,
      });
      
      closeAddMeetingDialog();
      
      setTimeout(() => setPostSuccess(false), 5000);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setPostingMeeting(false);
    }
  };

  const fetchMeetings = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://7549jb05eg.execute-api.us-east-1.amazonaws.com/prod/meetingSynctalent', {
        body: JSON.stringify({  talent_id: user.data[0].c_id || null }),
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(response.status === 401 ? 'Unauthorized: Please login again' : 'Failed to fetch meetings');
      }

      const data = await response.json();
      console.log('API Response:', data); // Debug log
      
      // Handle different possible response structures
      let meetingsArray = [];
      
      if (data && data.data && Array.isArray(data.data)) {
        // New structure: { status: 200, data: [...] }
        meetingsArray = data.data;
      } else if (data && data.body && data.body.data && data.body.data.transcripts) {
        // Old structure: { body: { data: { transcripts: [...] } } }
        meetingsArray = data.body.data.transcripts;
      } else if (Array.isArray(data)) {
        // Direct array
        meetingsArray = data;
      } else {
        console.error('Unexpected response structure:', data);
        throw new Error('Unexpected response structure from server');
      }

      // Filter and sort meetings
      const validMeetings = meetingsArray
        .filter(meeting => {
          // For new structure
          if (meeting.today_focus || meeting.accomplishments) return true;
          // For old structure
          if (meeting.summary && meeting.summary.short_summary) return true;
          return false;
        })
        .sort((a, b) => {
          // Use createdAt for new structure, dateString for old structure
          const dateA = new Date(a.createdAt || a.dateString);
          const dateB = new Date(b.createdAt || b.dateString);
          return dateB - dateA;
        });

      setMeetings(validMeetings);
      setFilteredMeetings(validMeetings);
    } catch (err) {
      console.error('Fetch meetings error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMeetings();
  }, []);

  // Debug effect to track pods state changes
  useEffect(() => {
    console.log('Pods state changed:', pods);
    console.log('Pods type:', typeof pods);
    console.log('Is pods array:', Array.isArray(pods));
    console.log('Pods length:', Array.isArray(pods) ? pods.length : 'N/A');
  }, [pods]);

  useEffect(() => {
    filterMeetings();
  }, [searchQuery, startDate, endDate, timeRange, selectedCandidate, statusFilter, contentFilter, meetings]);

  const filterMeetings = () => {
    let filtered = [...meetings];

    // Text search filter
    if (searchQuery) {
      filtered = filtered.filter(meeting => {
        // Handle new structure
        if (meeting.today_focus || meeting.accomplishments || meeting.pod_candidates || meeting.blockers) {
          return (
            meeting.today_focus?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            meeting.accomplishments?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            meeting.pod_candidates?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            meeting.blockers?.toLowerCase().includes(searchQuery.toLowerCase())
          );
        }
        // Handle old structure
        if (meeting.summary && meeting.summary.short_summary) {
          return meeting.summary.short_summary.toLowerCase().includes(searchQuery.toLowerCase());
        }
        return false;
      });
    }

    // Pod Candidates filter
    if (selectedCandidate !== 'all') {
      filtered = filtered.filter(meeting => 
        meeting.pod_candidates?.toLowerCase().includes(selectedCandidate.toLowerCase())
      );
    }

    // Status filter (based on blockers)
    if (statusFilter !== 'all') {
      filtered = filtered.filter(meeting => {
        if (statusFilter === 'blocked') {
          return meeting.blockers && meeting.blockers.trim() !== '';
        } else if (statusFilter === 'unblocked') {
          return !meeting.blockers || meeting.blockers.trim() === '';
        }
        return true;
      });
    }

    // Content completeness filter
    if (contentFilter !== 'all') {
      filtered = filtered.filter(meeting => {
        if (contentFilter === 'complete') {
          return meeting.today_focus && meeting.accomplishments && 
                 meeting.today_focus.trim() !== '' && meeting.accomplishments.trim() !== '';
        } else if (contentFilter === 'incomplete') {
          return !meeting.today_focus || !meeting.accomplishments ||
                 meeting.today_focus.trim() === '' || meeting.accomplishments.trim() === '';
        }
        return true;
      });
    }

    // Date filters
    if (startDate) {
      filtered = filtered.filter(meeting => {
        const meetingDate = new Date(meeting.createdAt || meeting.dateString);
        return meetingDate >= new Date(startDate);
      });
    }
    
    if (endDate) {
      filtered = filtered.filter(meeting => {
        const meetingDate = new Date(meeting.createdAt || meeting.dateString);
        return meetingDate <= new Date(endDate);
      });
    }

    // Time range filter
    if (timeRange !== 'all') {
      const now = new Date();
      let startDate = new Date();
      let endDate = new Date();
      
      switch (timeRange) {
        case 'week':
          startDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          endDate = new Date(now.getFullYear(), now.getMonth(), 0);
          break;
        case '3months':
          startDate = new Date(now.getFullYear(), now.getMonth() - 3, 1);
          endDate = new Date(now.getFullYear(), now.getMonth(), 0);
          break;
        default:
          break;
      }
      
      filtered = filtered.filter(meeting => {
        const meetingDate = new Date(meeting.createdAt || meeting.dateString);
        meetingDate.setHours(0, 0, 0, 0);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        return meetingDate >= startDate && meetingDate <= endDate;
      });
    }

    setFilteredMeetings(filtered);
    setPage(0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get unique candidates for filter dropdown
  const getUniqueCandidates = () => {
    const candidates = meetings
      .map(meeting => meeting.pod_candidates)
      .filter(candidate => candidate && candidate.trim() !== '')
      .map(candidate => candidate.trim());
    
    return [...new Set(candidates)].sort();
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSearchQuery('');
    setSelectedCandidate('all');
    setStatusFilter('all');
    setContentFilter('all');
    setTimeRange('all');
    setStartDate('');
    setEndDate('');
  };

  // Combine today's focus and accomplishments into one summary
  const getCombinedSummary = (meeting) => {
    let summary = '';
    
    // For new structure
    if (meeting.today_focus || meeting.accomplishments) {
      if (meeting.today_focus && meeting.today_focus.trim()) {
        summary += meeting.today_focus.trim();
      }
      if (meeting.accomplishments && meeting.accomplishments.trim()) {
        if (summary) summary += ' ';
        summary += meeting.accomplishments.trim();
      }
    }
    
    // For old structure
    if (!summary && meeting.summary?.short_summary) {
      summary = meeting.summary.short_summary;
    }
    
    return summary || 'No summary available';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress sx={{ color: theme.palette.primary.main }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Container sx={{ 
      py: 3,
      marginLeft: collapsed ? "calc(60px + (100vw - 60px) * 0.1)" : "calc(240px + (100vw - 240px) * 0.1)",
      marginRight: collapsed ? "calc((100vw - 60px) * 0.1)" : "calc((100vw - 240px) * 0.1)",
      width: collapsed ? 'calc((100vw - 60px) * 0.8)' : 'calc((100vw - 240px) * 0.8)',
      maxWidth: 'none',
      transition: 'all 0.3s ease',
    }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: 3,
          background: theme.palette.mode === 'dark' 
            ? 'linear-gradient(to right bottom, #1a1a1a, #2d2d2d)'
            : 'linear-gradient(to right bottom, #ffffff, #f8f9ff)',
          borderRadius: 2
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
          <Typography 
            variant="h4" 
            component="h1"
            sx={{ 
              color: theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.dark,
              fontWeight: 600,
              letterSpacing: '-0.5px'
            }}
          >
            {intl.formatMessage({ id: 'podSyncSummaries' })}
          </Typography>
          
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            {/* Add Meeting Button */}
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={openAddMeetingDialog}
              sx={{
                backgroundColor: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                }
              }}
            >
              {intl.formatMessage({ id: 'addMeeting' })}
            </Button>

            <Tooltip title="Refresh">
              <IconButton 
                onClick={fetchMeetings} 
                sx={{
                  backgroundColor: theme.palette.primary.light,
                  color: theme.palette.primary.dark,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.main,
                    color: '#fff'
                  }
                }}
              >
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Filters Section */}
        <Paper 
          elevation={1} 
          sx={{ 
            p: 2, 
            mb: 3,
            backgroundColor: theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.05)'
              : 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(10px)',
            borderRadius: 1
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder={intl.formatMessage({ id: 'searchInSummaries' })}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: theme.palette.background.paper,
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main,
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>{intl.formatMessage({ id: 'quickTimeRange' })}</InputLabel>
                <Select
                  value={timeRange}
                  onChange={(e) => {
                    setTimeRange(e.target.value);
                    if (e.target.value !== 'all') {
                      setStartDate('');
                      setEndDate('');
                    }
                  }}
                  label={intl.formatMessage({ id: 'quickTimeRange' })}
                  sx={{
                    backgroundColor: theme.palette.background.paper,
                    '&:hover': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                      }
                    }
                  }}
                >
                  <MenuItem value="all">{intl.formatMessage({ id: 'showAllTime' })}</MenuItem>
                  <MenuItem value="week">Last Week</MenuItem>
                  <MenuItem value="month">Last Month</MenuItem>
                  <MenuItem value="3months">Last 3 Months</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={2}>
                {['start', 'end'].map((type) => (
                  <TextField
                    key={type}
                    type="date"
                    label={`${type === 'start' ? intl.formatMessage({ id: 'startDate' }) : intl.formatMessage({ id: 'endDate' })}`}
                    value={type === 'start' ? startDate : endDate}
                    onChange={(e) => {
                      if (type === 'start') setStartDate(e.target.value);
                      else setEndDate(e.target.value);
                      setTimeRange('all');
                    }}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    disabled={timeRange !== 'all'}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: theme.palette.background.paper,
                        '&:hover fieldset': {
                          borderColor: theme.palette.primary.main,
                        }
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <CalendarMonth color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                ))}
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        {/* Table Section */}
        <Paper 
          sx={{ 
            width: '100%', 
            overflow: 'hidden',
            backgroundColor: theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.05)'
              : 'rgba(255, 255, 255, 0.9)',
            borderRadius: 1
          }}
        >
          <Table stickyHeader sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    backgroundColor: theme.palette.mode === 'dark' 
                      ? theme.palette.grey[900]
                      : theme.palette.primary.dark,
                    color: '#fff',
                    fontWeight: 600,
                    width: '30%',
                    pr: 3
                  }}
                >
                  <Box display="flex" alignItems="center">
                    <Event sx={{ mr: 1 }} />
                    {intl.formatMessage({ id: 'dateAndTime' })}
                  </Box>
                </TableCell>
                <TableCell 
                  sx={{
                    backgroundColor: theme.palette.mode === 'dark' 
                      ? theme.palette.grey[900]
                      : theme.palette.primary.dark,
                    color: '#fff',
                    fontWeight: 600,
                    width: '70%',
                    pl: 6
                  }}
                >
                  <Box display="flex" alignItems="center">
                    <Assignment sx={{ mr: 1 }} />
                    Summary
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredMeetings
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((meeting, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      backgroundColor: theme.palette.mode === 'dark'
                        ? index % 2 === 0 ? 'rgba(255, 255, 255, 0.02)' : 'transparent'
                        : index % 2 === 0 ? 'rgba(0, 0, 0, 0.02)' : 'transparent',
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark'
                          ? `${theme.palette.primary.dark}30`
                          : `${theme.palette.primary.light}30`,
                        transition: 'background-color 0.2s ease'
                      }
                    }}
                  >
                    <TableCell sx={{ 
                      color: theme.palette.text.primary, 
                      width: '30%', 
                      pr: 3,
                      wordWrap: 'break-word'
                    }}>
                      {formatDate(meeting.createdAt || meeting.dateString)}
                    </TableCell>
                    <TableCell sx={{ 
                      color: theme.palette.text.primary, 
                      width: '70%', 
                      pl: 6,
                      wordWrap: 'break-word'
                    }}>
                      <Tooltip title="View Summary">
                        <IconButton
                          onClick={() => setSelectedMeeting(meeting)}
                          sx={{
                            color: theme.palette.primary.main,
                            '&:hover': {
                              backgroundColor: `${theme.palette.primary.main}20`
                            }
                          }}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredMeetings.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary
            }}
          />
        </Paper>

        {/* Add Meeting Dialog */}
        <Dialog
          open={addMeetingDialog}
          onClose={closeAddMeetingDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(to right bottom, #1a1a1a, #2d2d2d)'
                : 'linear-gradient(to right bottom, #ffffff, #f8f9ff)',
            }
          }}
        >
          <DialogTitle sx={{ 
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.mode === 'dark'
              ? theme.palette.grey[900]
              : theme.palette.primary.dark,
            color: '#fff',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Box display="flex" alignItems="center">
              <Add sx={{ mr: 1 }} />
              {intl.formatMessage({ id: 'addMeeting' })}
            </Box>
            <IconButton
              onClick={closeAddMeetingDialog}
              sx={{ color: '#fff' }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ mt: 2, position: 'relative', minHeight: '300px' }}>
            {loadingPods ? (
              <Box 
                display="flex" 
                flexDirection="column" 
                alignItems="center" 
                justifyContent="center" 
                sx={{ 
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: theme.palette.mode === 'dark' 
                    ? 'rgba(0, 0, 0, 0.7)' 
                    : 'rgba(255, 255, 255, 0.7)',
                  backdropFilter: 'blur(2px)',
                  zIndex: 1000,
                  borderRadius: 1
                }}
              >
                <CircularProgress 
                  size={40} 
                  sx={{ 
                    color: theme.palette.primary.main,
                    mb: 2 
                  }} 
                />
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: theme.palette.text.primary,
                    mb: 1,
                    textAlign: 'center'
                  }}
                >
                  Loading Pods...
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: theme.palette.text.secondary,
                    textAlign: 'center'
                  }}
                >
                  Please wait while we fetch your available pods
                </Typography>
              </Box>
            ) : null}
            
            <Box sx={{ opacity: loadingPods ? 0.3 : 1 }}>
              {/* Instructions */}
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  Select pods and add meeting URLs. Each checked pod will receive its corresponding URL.
                  You can use the same URL for multiple pods or different URLs for each pod.
                </Typography>
              </Alert>

              {/* Pod Search */}
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search pods by name, ID, or ART name..."
                value={podSearchQuery}
                onChange={(e) => setPodSearchQuery(e.target.value)}
                disabled={loadingPods}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: theme.palette.background.paper,
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main,
                    }
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: podSearchQuery && (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setPodSearchQuery('')}
                        size="small"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Pods List */}
              <Box display="flex" flexDirection="column" gap={2}>
                {filteredPods.length === 0 && !loadingPods ? (
                  <Alert severity="warning">
                    <Typography>
                      {podSearchQuery.trim() ? 'No pods found matching your search' : 'No pods available'}
                    </Typography>
                  </Alert>
                ) : (
                  filteredPods.map((pod) => (
                    <Paper 
                      key={pod.pod_id}
                      elevation={1}
                      sx={{ 
                        p: 2, 
                        backgroundColor: theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)'
                          : 'rgba(0, 0, 0, 0.02)',
                        borderRadius: 2,
                        border: podEntries[pod.pod_id]?.checked 
                          ? `2px solid ${theme.palette.primary.main}` 
                          : `1px solid ${theme.palette.divider}`
                      }}
                    >
                      <Grid container spacing={2} alignItems="center">
                        {/* Checkbox and Pod Name */}
                        <Grid item xs={12} md={4}>
                          <Box display="flex" alignItems="center">
                            <Checkbox
                              checked={podEntries[pod.pod_id]?.checked || false}
                              onChange={(e) => updatePodEntry(pod.pod_id, 'checked', e.target.checked)}
                              disabled={loadingPods}
                              sx={{
                                color: theme.palette.primary.main,
                                '&.Mui-checked': {
                                  color: theme.palette.primary.main,
                                }
                              }}
                            />
                            <Box ml={1}>
                              <Typography 
                                variant="h6" 
                                sx={{ 
                                  color: podEntries[pod.pod_id]?.checked 
                                    ? theme.palette.primary.main 
                                    : theme.palette.text.primary,
                                  fontWeight: podEntries[pod.pod_id]?.checked ? 600 : 500
                                }}
                              >
                                {pod.pod_name}
                              </Typography>
                              <Typography 
                                variant="body2" 
                                color="text.secondary"
                              >
                                ID: {pod.pod_id} • {pod.art_name}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>

                        {/* Meeting URL Input */}
                        <Grid item xs={12} md={8}>
                          <TextField
                            fullWidth
                            label="Meeting URL"
                            placeholder="Enter meeting URL (required if checked)"
                            value={podEntries[pod.pod_id]?.url || ''}
                            onChange={(e) => updatePodEntry(pod.pod_id, 'url', e.target.value)}
                            disabled={loadingPods || !podEntries[pod.pod_id]?.checked}
                            required={podEntries[pod.pod_id]?.checked}
                            error={
                              podEntries[pod.pod_id]?.checked && 
                              (!podEntries[pod.pod_id]?.url || podEntries[pod.pod_id]?.url.trim() === '')
                            }
                            helperText={
                              podEntries[pod.pod_id]?.checked && 
                              (!podEntries[pod.pod_id]?.url || podEntries[pod.pod_id]?.url.trim() === '')
                                ? 'Meeting URL is required for checked pods'
                                : ''
                            }
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: podEntries[pod.pod_id]?.checked 
                                  ? theme.palette.background.paper
                                  : theme.palette.action.disabled,
                                '&:hover fieldset': {
                                  borderColor: podEntries[pod.pod_id]?.checked 
                                    ? theme.palette.primary.main
                                    : theme.palette.divider,
                                }
                              }
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  ))
                )}
              </Box>

              {/* Summary */}
              {Object.keys(podEntries).length > 0 && (
                <Box mt={3}>
                  <Typography variant="body2" color="text.secondary">
                    Selected: {Object.values(podEntries).filter(entry => entry.checked).length} pod(s)
                    {podSearchQuery.trim() && (
                      <> • Showing {filteredPods.length} of {safePods.length} pods</>
                    )}
                  </Typography>
                  {podSearchQuery.trim() && filteredPods.length > 0 && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      Clear search to see all pods
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button
              onClick={closeAddMeetingDialog}
              disabled={postingMeeting}
              sx={{ color: theme.palette.text.secondary }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={postMeetingUrl}
              disabled={postingMeeting || !validatePodEntries() || loadingPods}
              sx={{
                backgroundColor: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                }
              }}
            >
              {postingMeeting ? (
                <>
                  <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                  Adding Meetings...
                </>
              ) : loadingPods ? (
                'Loading Pods...'
              ) : (
                `Add to ${Object.values(podEntries).filter(entry => entry.checked).length} Pod${Object.values(podEntries).filter(entry => entry.checked).length !== 1 ? 's' : ''}`
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Summary Dialog */}
        <Dialog
          open={Boolean(selectedMeeting)}
          onClose={() => setSelectedMeeting(null)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(to right bottom, #1a1a1a, #2d2d2d)'
                : 'linear-gradient(to right bottom, #ffffff, #f8f9ff)',
            }
          }}
        >
          <DialogTitle sx={{ 
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.mode === 'dark'
              ? theme.palette.grey[900]
              : theme.palette.primary.dark,
            color: '#fff'
          }}>
            <Box display="flex" alignItems="center">
              <Assignment sx={{ mr: 1 }} />
              Summary
            </Box>
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            {selectedMeeting && (
              <Typography variant="body1" sx={{ 
                color: theme.palette.text.primary, 
                lineHeight: 1.6,
                fontSize: '1rem'
              }}>
                {getCombinedSummary(selectedMeeting)}
              </Typography>
            )}
          </DialogContent>
        </Dialog>
      </Paper>

      {/* Success Snackbar */}
      <Snackbar
        open={Boolean(postSuccess)}
        autoHideDuration={5000}
        onClose={() => setPostSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setPostSuccess(false)} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          {typeof postSuccess === 'object' ? postSuccess.message : 'Meeting URL posted successfully!'}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default MeetingSummariesTableTalent;